import { englishMultilingualTtsVoice, spanishTtsVoice } from '../../../../shared/constants/sparkConstants/voices';
import { carryVoiceStringPrompt, getVoiceStringPrompt, voiceStringsMatch } from '../../../../shared/utils/ttsVoiceString';

export interface SparkVoiceOption {
    value: string
    provider?: string
    voice?: string
    englishVoice?: string
    spanishVoice?: string
    [key: string]: any
}

export interface SparkTtsOption {
    ttsProvider: string
    ttsVoice: string
}

const isSpanishLocale = (locale?: string): boolean =>
    (locale || '').toLowerCase().startsWith('es');

const providersMatch = (optionProvider?: string, ttsProvider?: string): boolean =>
    !optionProvider || !ttsProvider || optionProvider.toLowerCase() === ttsProvider.toLowerCase();

export const getVoiceStringForLocale = (
    option: SparkVoiceOption | null | undefined,
    locale: string,
    fallbackVoice: string
): string => {
    if (!option) {
        return fallbackVoice;
    }

    if (option.value === 'custom') {
        return option.voice || fallbackVoice;
    }

    if (isSpanishLocale(locale)) {
        return option.spanishVoice || fallbackVoice;
    }

    return option.englishVoice || option.voice || fallbackVoice;
};

export const findVoiceOptionByValue = (
    options: SparkVoiceOption[] | null | undefined,
    value: string
): SparkVoiceOption | undefined =>
    (options || []).find(option => option.value === value);

export const findVoiceOptionByTts = (
    options: SparkVoiceOption[] | null | undefined,
    ttsOption: Partial<SparkTtsOption> | null | undefined
): SparkVoiceOption | undefined => {
    if (!ttsOption?.ttsVoice) {
        return undefined;
    }

    return (options || []).find(option => {
        const configuredVoices = [option.englishVoice, option.spanishVoice, option.voice].filter(Boolean);
        return providersMatch(option.provider, ttsOption.ttsProvider)
            && configuredVoices.some(voice => voiceStringsMatch(voice, ttsOption.ttsVoice));
    });
};

export const findVoiceOptionForLocaleDisplay = (
    options: SparkVoiceOption[] | null | undefined,
    ttsOption: Partial<SparkTtsOption> | null | undefined,
    locale: string,
    companionEnglishTtsOption?: Partial<SparkTtsOption> | null,
    assistantType?: string
): SparkVoiceOption | undefined => {
    if (isSpanishLocale(locale) && ttsOption?.ttsVoice) {
        const companionOption = findVoiceOptionByTts(options, companionEnglishTtsOption);
        if (
            companionOption &&
            voiceStringsMatch(getVoiceStringForLocale(companionOption, locale, spanishTtsVoice), ttsOption.ttsVoice)
        ) {
            return companionOption;
        }

        const defaultVoiceName = assistantType === 'dental-scheduling' ? 'Callie' : 'Hope Classic';
        const verticalDefaultOption = findVoiceOptionByValue(options, defaultVoiceName);
        if (
            verticalDefaultOption &&
            voiceStringsMatch(getVoiceStringForLocale(verticalDefaultOption, locale, spanishTtsVoice), ttsOption.ttsVoice)
        ) {
            return verticalDefaultOption;
        }
    }

    return findVoiceOptionByTts(options, ttsOption);
};

export const mapTtsOptionsToLocale = (
    ttsOptions: SparkTtsOption[] | null | undefined,
    voiceOptions: SparkVoiceOption[] | null | undefined,
    locale: string
): SparkTtsOption[] => {
    const fallbackVoice = isSpanishLocale(locale) ? spanishTtsVoice : englishMultilingualTtsVoice;

    return (ttsOptions || []).map(ttsOption => {
        const selectedOption = findVoiceOptionByTts(voiceOptions, ttsOption);
        if (!selectedOption) {
            if (!isSpanishLocale(locale)) {
                return { ...ttsOption };
            }

            return {
                ttsProvider: 'ElevenLabs',
                ttsVoice: fallbackVoice
            };
        }

        return {
            ttsProvider: selectedOption.provider || ttsOption.ttsProvider || 'ElevenLabs',
            ttsVoice: carryVoiceStringPrompt(
                getVoiceStringForLocale(selectedOption, locale, fallbackVoice),
                getVoiceStringPrompt(ttsOption.ttsVoice)
            )
        };
    });
};
